import { Box, MenuItem, Select } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import { DoubleTextBlueCard } from '../../../shared/components/doubleTextBlueCard';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import ImproveTreatment from '../../../assets/icons/improveTreatment.svg';
import {
  getDiseaseOptions,
  getOtherValues
} from '../../../shared/utils/peng2018';
import {
  setPengDisease,
  setPengReeSecondVal
} from '../../../store/slices/metaDataSlice';
import CustomArrowIcon from '../../../shared/components/customSelectArrow';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import {
  displayCalculation,
  displayLabel1otherOverweightAbove65Calculation,
  roundToPrecision
} from '../../../shared/utils/utilFunctions';

export const PengDiseaseScreen = ({
  handleIconClick,
  testId
}: {
  handleIconClick: () => void;
  testId?: string;
}) => {
  const dispatch = useDispatch();

  const { estimatingRequirements, patientInformation } =
    useAppSelector(metaDataSelector);
  const { peng2018 } = estimatingRequirements;
  const { disease, ree2ndVal } = peng2018;
  const { calcValues } = disease;
  const { metric } = patientInformation;
  const { weight, estimatedDryWeight, height } = metric;
  const { bmi, age, gender } = patientInformation;
  const { years: ageOfPatient } = age;

  const consideredWeight = estimatedDryWeight > 0 ? estimatedDryWeight : weight;

  // calculating REE 2nd value
  useEffect(() => {
    if (calcValues && calcValues.length > 0) {
      // REE 2nd val: REE 1st val * dryWeight/weight
      // check if dry weight is available else take weight

      const ree2ndValToBeUpdated = calcValues.map((value) =>
        roundToPrecision(value * consideredWeight)
      );

      dispatch(setPengReeSecondVal(ree2ndValToBeUpdated));
    }
  }, [disease]);

  const [selectedDiseaseId, setSelectedDiseaseId] = useState(
    disease.id != undefined ? disease.id : ''
  );

  // Compute disease list from the local BMI and Age
  const diseaseOptions = getDiseaseOptions(bmi, ageOfPatient);

  const handleDiseaseChange = (e) => {
    const selectedId = e.target.value; // e.g., 28 or null
    setSelectedDiseaseId(selectedId);

    const consideredWeight =
      estimatedDryWeight > 0 ? estimatedDryWeight : weight;

    // find the entire disease object from the array
    const selectedObj = diseaseOptions.find((d) => d.id === selectedId);
    if (!selectedObj) return;
    // Dispatch the full object to Redux

    if (selectedObj.label === 'Other') {
      // We'll compute the array
      const otherVals = getOtherValues({
        bmi: bmi,
        age: ageOfPatient,
        weight: consideredWeight, //storedWeight,
        height: height, //storedHeight,
        gender: gender //storedGender,
      });

      const newObj = {
        ...selectedObj,
        calcValues: otherVals
      };

      dispatch(setPengDisease(newObj));
    } else {
      dispatch(setPengDisease(selectedObj));
    }
  };
  return (
    <>
      <Box sx={{ textAlign: 'center', mb: 2, mt: 5 }}>
        <TextWithInfoIcon
          title={'Select Disease State'}
          icon={InfoIcon}
          fontSize={'1.125rem'}
          lineHeight={'1.5rem'}
          variant={'h4'}
          handleIconClick={handleIconClick}
          testId={testId}
        />

        <Select
          fullWidth
          value={selectedDiseaseId}
          onChange={handleDiseaseChange}
          displayEmpty
          IconComponent={CustomArrowIcon}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            PaperProps: {
              sx: {
                // Ensures the menu matches the select’s width
                // (MUI usually does this by default, but you can force if needed)
                width: 'auto',
                // Optionally add a border or remove shadow:
                border: '0.0625rem solid #D9D9D6',
                boxShadow: 'none',
                mt: 1
              }
            }
          }}
          sx={{
            backgroundColor: '#fff',
            fontFamily: 'Calibri',
            fontWeight: 400,
            lineHeight: '1.375rem',
            fontSize: '1.125rem',
            // Conditionally set color based on whether a value is selected
            color: selectedDiseaseId ? '#002A3A' : '#D9D9D6',
            textAlign: 'left',
            height: '42px',
            border: '0.0625rem solid #D9D9D6',
            // Remove focus outline
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'transparent'
              },
            // Optionally remove hover/focus highlight
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent'
            }
          }}
        >
          {/* Disabled placeholder with lighter color */}
          <MenuItem disabled value="" sx={{ color: '#D9D9D6' }}>
            Select Disease State
          </MenuItem>

          {/* Normal items in dark color */}
          {diseaseOptions.map((disease, idx) => (
            <MenuItem
              sx={{
                color: '#002A3A',
                fontFamily: 'Calibri',
                fontWeight: 400,
                lineHeight: '1.375rem',
                fontSize: '1.125rem'
              }}
              key={idx}
              value={disease.id}
            >
              {disease.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 5 }}>
        <DoubleTextBlueCard
          heading1="REE"
          label1={
            disease?.id !== 'otherOverweightAbove65' &&
            disease?.id !== 'otherOverweightBelow65'
              ? displayCalculation(disease?.calcValues)
              : displayLabel1otherOverweightAbove65Calculation(
                  calcValues,
                  consideredWeight
                )
          }
          unit1={' kcal/kg/day'}
          label2={
            disease?.id !== 'otherOverweightAbove65' &&
            disease?.id !== 'otherOverweightBelow65'
              ? displayCalculation(ree2ndVal)
              : displayCalculation(calcValues)
          }
          unit2={' kcal/day'}
          icon={ImproveTreatment}
        />
      </Box>
    </>
  );
};
